import React from 'react'

import {
    Chip,
    CircularProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AcUnitIcon from '@material-ui/icons/AcUnit'
import ApartmentIcon from '@material-ui/icons/Apartment'
import BuildIcon from '@material-ui/icons/Build'
import FlashOnIcon from '@material-ui/icons/FlashOn'
import FormatPaintIcon from '@material-ui/icons/FormatPaint'
import KitchenIcon from '@material-ui/icons/Kitchen'
import LayersIcon from '@material-ui/icons/Layers'
import OpacityIcon from '@material-ui/icons/Opacity'
import VpnKeyIcon from '@material-ui/icons/VpnKey'
import { SvgIconComponent } from '@material-ui/icons'

import { useTimezone } from '../../contexts/timezone'
import {
    RecordsOccupancy,
    RecordsServiceRequestCategory,
    RecordsServiceRequestRow,
    RecordsUnitRow,
    RecordsUnitsSummary,
} from '../../hooks/useRecords'
import { RecordsOccupancyFilter } from './paths'

interface AllUnitsPaneProps {
    summary: RecordsUnitsSummary | undefined
    units: RecordsUnitRow[]
    categories: RecordsServiceRequestCategory[]
    requests: RecordsServiceRequestRow[]
    loading: boolean
    category: string | null
    openOnly: boolean
    occupancy: RecordsOccupancyFilter | null
    onSelectCategory: (key: string | null) => void
    onSelectOpen: (open: boolean) => void
    onSelectOccupancy: (occupancy: RecordsOccupancyFilter | null) => void
    onSelectUnit: (unitId: number) => void
}

type CategoryStyle = {
    label: string
    color: string
    bg: string
    Icon: SvgIconComponent
}

const CATEGORY_STYLES: Record<string, CategoryStyle> = {
    PLUMBING: {
        label: 'Plumbing',
        color: '#1565c0',
        bg: 'rgba(21, 101, 192, 0.12)',
        Icon: OpacityIcon,
    },
    ELECTRICAL: {
        label: 'Electrical',
        color: '#f9a825',
        bg: 'rgba(249, 168, 37, 0.16)',
        Icon: FlashOnIcon,
    },
    HVAC: {
        label: 'HVAC',
        color: '#00838f',
        bg: 'rgba(0, 131, 143, 0.12)',
        Icon: AcUnitIcon,
    },
    APPLIANCES: {
        label: 'Appliances',
        color: '#6a1b9a',
        bg: 'rgba(106, 27, 154, 0.12)',
        Icon: KitchenIcon,
    },
    HARDWARE_LOCKS: {
        label: 'Hardware/Locks',
        color: '#5d4037',
        bg: 'rgba(93, 64, 55, 0.12)',
        Icon: VpnKeyIcon,
    },
    FLOORING: {
        label: 'Flooring',
        color: '#558b2f',
        bg: 'rgba(85, 139, 47, 0.14)',
        Icon: LayersIcon,
    },
    PAINTING: {
        label: 'Painting',
        color: '#c62828',
        bg: 'rgba(198, 40, 40, 0.12)',
        Icon: FormatPaintIcon,
    },
    OTHER: {
        label: 'Other',
        color: '#546e7a',
        bg: 'rgba(84, 110, 122, 0.14)',
        Icon: BuildIcon,
    },
}

const STATUS_STYLES: Record<
    string,
    { label: string; color: string; bg: string }
> = {
    SUBMITTED: {
        label: 'Submitted',
        color: '#e65100',
        bg: 'rgba(230, 81, 0, 0.12)',
    },
    ACKNOWLEDGED: {
        label: 'Acknowledged',
        color: '#1565c0',
        bg: 'rgba(21, 101, 192, 0.12)',
    },
    IN_PROGRESS: {
        label: 'In Progress',
        color: '#6a1b9a',
        bg: 'rgba(106, 27, 154, 0.12)',
    },
    COMPLETED: {
        label: 'Completed',
        color: '#2e7d32',
        bg: 'rgba(46, 125, 50, 0.12)',
    },
}

const OCCUPANCY_STYLES: Record<
    RecordsOccupancy,
    { label: string; color: string; bg: string }
> = {
    occupied: {
        label: 'Occupied',
        color: '#2e7d32',
        bg: 'rgba(46, 125, 50, 0.12)',
    },
    future: {
        label: 'Future',
        color: '#1565c0',
        bg: 'rgba(21, 101, 192, 0.12)',
    },
    vacant: {
        label: 'Vacant',
        color: '#546e7a',
        bg: 'rgba(84, 110, 122, 0.14)',
    },
}

const getCategoryStyle = (key: string): CategoryStyle =>
    CATEGORY_STYLES[key] || CATEGORY_STYLES.OTHER

const unitOccupancy = (unit: RecordsUnitRow): RecordsOccupancy =>
    unit.occupancy || (unit.occupied ? 'occupied' : 'vacant')

const getStatusStyle = (status: string) => {
    if (STATUS_STYLES[status]) return STATUS_STYLES[status]
    const label = status
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    return {
        label: label || 'Unknown',
        color: '#546e7a',
        bg: 'rgba(84, 110, 122, 0.14)',
    }
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
    },
    header: {
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        flexShrink: 0,
    },
    title: {
        fontWeight: 700,
        marginBottom: theme.spacing(2),
    },
    tiles: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    tile: {
        minWidth: 120,
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        textAlign: 'left',
    },
    tileButton: {
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        display: 'block',
        '&:hover': {
            borderColor: theme.palette.primary.light,
        },
    },
    tileActive: {
        borderColor: theme.palette.primary.main,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
        backgroundColor: 'rgba(25, 118, 210, 0.06)',
    },
    tileValue: {
        fontSize: '1.25rem',
        fontWeight: 700,
        fontFamily: 'monospace',
        lineHeight: 1.2,
    },
    tileLabel: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
    },
    listWrap: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        padding: theme.spacing(1.5, 2, 3),
    },
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
    },
    card: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        width: '100%',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        padding: theme.spacing(1.5, 1.75),
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        borderLeftWidth: 4,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        transition:
            'border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
        '&:hover': {
            borderColor: theme.palette.primary.light,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            backgroundColor: 'rgba(25, 118, 210, 0.03)',
        },
        '&:disabled': {
            cursor: 'default',
            opacity: 0.7,
            '&:hover': {
                borderColor: theme.palette.divider,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                backgroundColor: theme.palette.background.paper,
            },
        },
    },
    iconWrap: {
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cardBody: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    cardTitle: {
        fontWeight: 600,
        fontSize: '0.95rem',
        lineHeight: 1.35,
        color: theme.palette.text.primary,
    },
    cardChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        alignItems: 'center',
    },
    metaChip: {
        height: 22,
        fontWeight: 600,
        fontSize: '0.7rem',
    },
    cardMeta: {
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
        lineHeight: 1.4,
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(6),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    emptyIcon: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: theme.palette.grey[200],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(2),
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(6),
    },
}))

const occupancyLabel = (occupancy: RecordsOccupancyFilter | null) => {
    if (occupancy === 'occupied') return 'Occupied units'
    if (occupancy === 'future') return 'Future units'
    if (occupancy === 'vacant') return 'Vacant units'
    return 'All units'
}

export const AllUnitsPane = ({
    summary,
    units,
    categories,
    requests,
    loading,
    category,
    openOnly,
    occupancy,
    onSelectCategory,
    onSelectOpen,
    onSelectOccupancy,
    onSelectUnit,
}: AllUnitsPaneProps) => {
    const classes = useStyles()
    const { formatDate } = useTimezone()
    const showingRequests = Boolean(category) || openOnly
    const showingOccupancy = !showingRequests
    const occupancyUnits = occupancy
        ? units.filter((unit) => unitOccupancy(unit) === occupancy)
        : units

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Typography variant="h6" className={classes.title}>
                    {occupancyLabel(showingOccupancy ? occupancy : null)}
                </Typography>
                <div className={classes.tiles}>
                    <button
                        type="button"
                        className={`${classes.tile} ${classes.tileButton} ${
                            showingOccupancy && occupancy == null
                                ? classes.tileActive
                                : ''
                        }`}
                        onClick={() => onSelectOccupancy(null)}
                    >
                        <div className={classes.tileValue}>
                            {summary?.unit_count ?? '—'}
                        </div>
                        <div className={classes.tileLabel}>Units</div>
                    </button>
                    <button
                        type="button"
                        className={`${classes.tile} ${classes.tileButton} ${
                            occupancy === 'occupied' ? classes.tileActive : ''
                        }`}
                        onClick={() =>
                            onSelectOccupancy(
                                occupancy === 'occupied' ? null : 'occupied',
                            )
                        }
                    >
                        <div className={classes.tileValue}>
                            {summary?.occupied_count ?? '—'}
                        </div>
                        <div className={classes.tileLabel}>Occupied</div>
                    </button>
                    <button
                        type="button"
                        className={`${classes.tile} ${classes.tileButton} ${
                            occupancy === 'future' ? classes.tileActive : ''
                        }`}
                        onClick={() =>
                            onSelectOccupancy(
                                occupancy === 'future' ? null : 'future',
                            )
                        }
                    >
                        <div className={classes.tileValue}>
                            {summary?.future_count ?? '—'}
                        </div>
                        <div className={classes.tileLabel}>Future</div>
                    </button>
                    <button
                        type="button"
                        className={`${classes.tile} ${classes.tileButton} ${
                            occupancy === 'vacant' ? classes.tileActive : ''
                        }`}
                        onClick={() =>
                            onSelectOccupancy(
                                occupancy === 'vacant' ? null : 'vacant',
                            )
                        }
                    >
                        <div className={classes.tileValue}>
                            {summary?.vacant_count ?? '—'}
                        </div>
                        <div className={classes.tileLabel}>Vacant</div>
                    </button>
                    <button
                        type="button"
                        className={`${classes.tile} ${classes.tileButton} ${
                            openOnly && !category ? classes.tileActive : ''
                        }`}
                        onClick={() => onSelectOpen(!(openOnly && !category))}
                    >
                        <div className={classes.tileValue}>
                            {summary?.open_service_request_count ?? '—'}
                        </div>
                        <div className={classes.tileLabel}>Open requests</div>
                    </button>
                </div>
                {showingRequests ? (
                    <div className={classes.chips}>
                        {categories.map((item) => (
                            <Chip
                                key={item.key}
                                size="small"
                                clickable
                                color={
                                    category === item.key
                                        ? 'primary'
                                        : 'default'
                                }
                                label={`${item.label} (${item.total})`}
                                onClick={() =>
                                    onSelectCategory(
                                        category === item.key ? null : item.key,
                                    )
                                }
                                style={{ fontWeight: 600 }}
                            />
                        ))}
                    </div>
                ) : null}
            </div>
            <div className={classes.listWrap}>
                {loading ? (
                    <div className={classes.loading}>
                        <CircularProgress size={28} />
                    </div>
                ) : showingRequests ? (
                    requests.length === 0 ? (
                        <div className={classes.empty}>
                            No service requests match this filter.
                        </div>
                    ) : (
                        <div className={classes.cardList}>
                            {requests.map((row) => {
                                const cat = getCategoryStyle(row.category)
                                const status = getStatusStyle(row.status)
                                const Icon = cat.Icon
                                const title =
                                    row.issue_title ||
                                    row.description ||
                                    'Service request'
                                const dateLabel = row.created_date
                                    ? formatDate(row.created_date, 'date-only')
                                    : ''
                                const showCategoryChip =
                                    !category || category !== row.category

                                return (
                                    <button
                                        type="button"
                                        key={row.id}
                                        className={classes.card}
                                        style={{ borderLeftColor: cat.color }}
                                        disabled={row.unit_id == null}
                                        onClick={() => {
                                            if (row.unit_id != null) {
                                                onSelectUnit(row.unit_id)
                                            }
                                        }}
                                    >
                                        <div
                                            className={classes.iconWrap}
                                            style={{
                                                backgroundColor: cat.bg,
                                                color: cat.color,
                                            }}
                                        >
                                            <Icon style={{ fontSize: 22 }} />
                                        </div>
                                        <div className={classes.cardBody}>
                                            <div className={classes.cardTitle}>
                                                {title}
                                            </div>
                                            <div className={classes.cardChips}>
                                                {row.unit_name ? (
                                                    <Chip
                                                        size="small"
                                                        label={row.unit_name}
                                                        className={
                                                            classes.metaChip
                                                        }
                                                        icon={
                                                            <ApartmentIcon
                                                                style={{
                                                                    fontSize: 14,
                                                                }}
                                                            />
                                                        }
                                                    />
                                                ) : null}
                                                <Chip
                                                    size="small"
                                                    label={status.label}
                                                    className={classes.metaChip}
                                                    style={{
                                                        backgroundColor:
                                                            status.bg,
                                                        color: status.color,
                                                    }}
                                                />
                                                {showCategoryChip ? (
                                                    <Chip
                                                        size="small"
                                                        label={cat.label}
                                                        className={
                                                            classes.metaChip
                                                        }
                                                        style={{
                                                            backgroundColor:
                                                                cat.bg,
                                                            color: cat.color,
                                                        }}
                                                    />
                                                ) : null}
                                            </div>
                                            {(dateLabel || row.tenant_name) && (
                                                <div
                                                    className={classes.cardMeta}
                                                >
                                                    {[
                                                        dateLabel,
                                                        row.tenant_name,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' · ')}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    )
                ) : occupancyUnits.length === 0 ? (
                    <div className={classes.empty}>
                        No units match this occupancy filter.
                    </div>
                ) : (
                    <div className={classes.cardList}>
                        {occupancyUnits.map((unit) => {
                            const status = OCCUPANCY_STYLES[unitOccupancy(unit)]
                            const lastWork = unit.last_work_at
                                ? formatDate(unit.last_work_at, 'date-only')
                                : ''
                            return (
                                <button
                                    type="button"
                                    key={unit.id}
                                    className={classes.card}
                                    style={{ borderLeftColor: status.color }}
                                    onClick={() => onSelectUnit(unit.id)}
                                >
                                    <div
                                        className={classes.iconWrap}
                                        style={{
                                            backgroundColor: status.bg,
                                            color: status.color,
                                        }}
                                    >
                                        <ApartmentIcon
                                            style={{ fontSize: 22 }}
                                        />
                                    </div>
                                    <div className={classes.cardBody}>
                                        <div className={classes.cardTitle}>
                                            {unit.name}
                                        </div>
                                        <div className={classes.cardChips}>
                                            <Chip
                                                size="small"
                                                label={status.label}
                                                className={classes.metaChip}
                                                style={{
                                                    backgroundColor: status.bg,
                                                    color: status.color,
                                                }}
                                            />
                                            {unit.open_service_request_count >
                                            0 ? (
                                                <Chip
                                                    size="small"
                                                    label={`${
                                                        unit.open_service_request_count
                                                    } open request${
                                                        unit.open_service_request_count ===
                                                        1
                                                            ? ''
                                                            : 's'
                                                    }`}
                                                    className={classes.metaChip}
                                                />
                                            ) : null}
                                        </div>
                                        {(unit.folder_path || lastWork) && (
                                            <div className={classes.cardMeta}>
                                                {[
                                                    unit.folder_path,
                                                    lastWork
                                                        ? `Last work ${lastWork}`
                                                        : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}
